import React, {useEffect, useRef, useState} from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import logoHeader from '../img/icon.png';
import {mobileUserService} from "../services/mobileUserService";

interface VerificationStatus {
    status: 'loading' | 'success' | 'error';
    message: string;
}

const EmailVerification: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const { t } = useTranslation();
    const { isRTL } = useLanguage();
    const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>({
        status: 'loading',
        message: ''
    });
    const hasVerified = useRef(false);

    useEffect(() => {
        const verifyEmail = async () => {
            if (!token) {
                setVerificationStatus({
                    status: 'error',
                    message: t('verification.invalidToken')
                });
                return;
            }
            if (hasVerified.current) {
                console.log('⏭️ Skip - déjà vérifié');
                return;
            }

            hasVerified.current = true;
            try {
                console.log('🔍 Vérification du token:', token);

                // Appel direct avec fetch pour debug
                const response :any = await mobileUserService.validateUser(token);

                console.log('📡 Status de la réponse:', response.status);



                if (response.success) {
                    setVerificationStatus({
                        status: 'success',
                        message:  t('verification.success')
                    });

                    // Tentative d'ouverture automatique de l'app après 1 seconde
                    setTimeout(() => {
                        console.log('🚀 Tentative d\'ouverture de l\'app...');
                        window.location.href = 'inspecly://verify-email?status=success';
                    }, 1000);

                } else {
                    setVerificationStatus({
                        status: 'error',
                        message:  t('verification.error')
                    });
                }
            } catch (error: any) {
                console.error('❌ Erreur lors de la vérification:', error);

                setVerificationStatus({
                    status: 'error',
                    message: error?.message || t('verification.networkError')
                });
            }
        };

        verifyEmail();
    }, [token, t]);

    const handleOpenApp = () => {
        console.log('📱 Tentative d\'ouverture de l\'app manuellement');

        window.location.href = 'https://play.google.com/store/apps/details?id=com.inspecly.mobile';
        // Fallback vers le Play Store après 2 secondes si l'app ne s'ouvre pas
        setTimeout(() => {
            if (document.hasFocus()) {
                console.log('⬇️ App non installée, redirection vers Play Store');
                window.location.href = 'https://play.google.com/store/apps/details?id=com.inspecly.mobile';
            }
        }, 2000);
    };

    return (
        <div className={`min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center px-4 ${isRTL ? 'rtl' : 'ltr'}`}>
            <div className="max-w-md w-full">
                {/* Logo */}
                <div className="text-center mb-8">
                    <img
                        src={logoHeader}
                        alt="Inspecly Logo"
                        className="h-20 w-auto mx-auto mb-4"
                    />
                </div>

                {/* Carte de vérification */}
                <div className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-[#115DF2]/20">

                    {/* Loading State */}
                    {verificationStatus.status === 'loading' && (
                        <div className="text-center">
                            <div className="w-20 h-20 bg-gradient-to-br from-[#115DF2] to-[#3678FF] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                                <Loader className="w-10 h-10 text-white animate-spin" />
                            </div>
                            <h1 className="text-3xl font-bold text-[#0A337F] mb-4">
                                {t('verification.loading') }
                            </h1>
                            <p className="text-lg text-gray-600">
                                {t('verification.loadingMessage') }
                            </p>
                        </div>
                    )}

                    {/* Success State */}
                    {verificationStatus.status === 'success' && (
                        <div className="text-center">
                            <div className="w-20 h-20 bg-gradient-to-br from-[#115DF2] to-[#3678FF] rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                                <CheckCircle className="w-12 h-12 text-white" />
                            </div>
                            <h1 className="text-3xl font-bold text-[#0A337F] mb-4">
                                {t('verification.successTitle')}
                            </h1>
                            <p className="text-lg text-gray-600 mb-8">
                                {verificationStatus.message}
                            </p>
                        </div>
                    )}

                    {/* Error State */}
                    {verificationStatus.status === 'error' && (
                        <div className="text-center">
                            <div className="w-20 h-20 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                                <XCircle className="w-12 h-12 text-white" />
                            </div>
                            <h1 className="text-3xl font-bold text-[#0A337F] mb-4">
                                {t('verification.errorTitle') }
                            </h1>
                            <p className="text-lg text-red-600 mb-8">
                                {verificationStatus.message}
                            </p>

                            {/* Retour à l'accueil */}
                            <Link
                                to="/"
                                className="block w-full px-8 py-4 border-2 border-[#115DF2] text-[#0A337F] rounded-xl font-bold text-lg hover:bg-[#115DF2]/10 transition-colors"
                            >
                                {t('verification.backToHome')}
                            </Link>
                        </div>
                    )}
                </div>

                {/* Footer Info */}
                <div className="text-center mt-8">
                    <p className="text-sm text-[#0A337F]/60">
                        © {new Date().getFullYear()} Inspecly
                    </p>
                </div>
            </div>
        </div>
    );
};

export default EmailVerification;